import React from "react";
import { Link } from "react-router-dom";
import { FiSettings, FiRepeat, FiLink, FiHeadphones, FiArrowRight } from "react-icons/fi";

const capabilities = [
  {
    icon: FiSettings,
    title: "Business Central Implementation",
    description:
      "End-to-end planning, configuration, customization, testing, training and go-live assistance.",
    iconBg: "bg-yellow-50 text-slate-950",
    topBorder: "border-t-[#FFDB16]",
  },
  {
    icon: FiRepeat,
    title: "Tally to Business Central Migration",
    description:
      "Structured master and transaction migration with validation, reconciliation and parallel-run support.",
    iconBg: "bg-yellow-50 text-slate-950",
    topBorder: "border-t-[#FFDB16]",
  },
  {
    icon: FiLink,
    title: "Integration & Automation",
    description:
      "Connect Business Central with business applications, reporting systems and operational workflows.",
    iconBg: "bg-yellow-50 text-slate-950",
    topBorder: "border-t-[#FFDB16]",
  },
  {
    icon: FiHeadphones,
    title: "Ongoing Support",
    description:
      "Reliable functional and technical assistance to keep systems stable, efficient and scalable.",
    iconBg: "bg-yellow-50 text-slate-950",
    topBorder: "border-t-[#FFDB16]",
  },
];

const About = () => {
  return (
    <section
      id="who-we-are"
      className="relative overflow-hidden scroll-mt-24 bg-gradient-to-b from-slate-50 to-slate-100 py-20 md:py-24 lg:pt-28 lg:pb-20"
    >
      {/* Dot pattern */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(100,116,139,0.4) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
        aria-hidden="true"
      />

      {/* Ambient glows */}
      <div
        className="pointer-events-none absolute -right-32 top-1/4 h-[24rem] w-[24rem] -translate-y-1/2 rounded-full bg-[#FFDB16]/[0.08] blur-[100px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-32 bottom-1/4 h-[24rem] w-[24rem] rounded-full bg-[#FFDB16]/[0.06] blur-[100px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-[1340px] px-5 md:px-8 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-[0.55fr_0.45fr] lg:gap-16">
          {/* Left column */}
          <div className="about-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold tracking-[0.14em] text-slate-700 uppercase shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FFDB16]" />
              ABOUT ABSCOD INFORMATICS
            </span>

            <h2 className="mt-6 font-raleway text-[clamp(1.6rem,3.5vw,2.5rem)] font-extrabold leading-[1.15] tracking-tight text-slate-900">
              The right{" "}
              <span className="relative whitespace-nowrap text-[#FFDB16]">
                <span className="relative z-10">technology partner</span>
                <span className="absolute inset-x-0 bottom-1 z-0 h-3 bg-[#FFDB16]/20 -skew-x-6" />
              </span>{" "}
              for your Business Central journey.
            </h2>

            <p className="mt-5 font-openSans text-[0.95rem] leading-relaxed text-slate-600">
              Abscod Informatics helps growing businesses modernize finance,
              operations, inventory and reporting with Microsoft Dynamics 365
              Business Central. We combine business-process understanding with
              practical technical expertise to deliver solutions that fit the way
              each organization actually works.
            </p>

            <p className="mt-3.5 font-openSans text-[0.95rem] leading-relaxed text-slate-600">
              From requirement analysis and system configuration to Tally
              migration, integrations, user training and post-go-live support, we
              manage the complete implementation journey with clarity,
              accountability and measurable outcomes.
            </p>

            <Link
              to="/contactus"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-[#FFDB16] px-6 py-3 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_rgba(255,219,22,0.25)] transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_14px_40px_rgba(255,219,22,0.35)]"
            >
              Start Your Transformation
              <FiArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                strokeWidth={2.5}
              />
            </Link>

            {/* Capability cards */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {capabilities.map((card, index) => (
                <article
                  key={card.title}
                  className={`about-fade-up about-delay-${index + 1} group rounded-2xl border border-slate-200 border-t-[3px] ${card.topBorder} bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:bg-slate-50`}
                >
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${card.iconBg} transition-transform duration-300 group-hover:scale-110`}
                  >
                    <card.icon className="h-[18px] w-[18px]" strokeWidth={2} />
                  </span>
                  <h3 className="mt-3 font-raleway text-[0.85rem] font-bold leading-snug text-slate-900">
                    {card.title}
                  </h3>
                  <p className="mt-1.5 font-openSans text-[0.78rem] leading-relaxed text-slate-500">
                    {card.description}
                  </p>
                </article>
              ))}
            </div>
          </div>

          {/* Right column visual */}
          <div className="about-fade-up about-delay-2 relative mx-auto w-full max-w-xl lg:mx-0 lg:max-w-none">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[36px] bg-gradient-to-br from-[#FFDB16]/15 via-[#FFDB16]/5 to-[#FFDB16]/15 blur-3xl" />
              <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-1.5 shadow-2xl shadow-slate-900/10">
                <div className="relative overflow-hidden rounded-[20px]">
                  <img
                    src="/Images/About.png"
                    alt="Abscod Informatics — Microsoft Dynamics 365 Business Central implementation and ERP services"
                    className="h-[340px] w-full object-cover transition-transform duration-500 ease-out hover:scale-[1.02] md:h-[460px] lg:h-[560px]"
                    style={{ objectPosition: "50% 60%" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent" />
                </div>
              </div>

              {/* Floating stat card */}
              <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-xl shadow-slate-900/10 sm:block lg:-left-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFDB16] text-slate-950">
                    <FiSettings className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <div>
                    <p className="font-raleway text-xl font-extrabold text-slate-900">100%</p>
                    <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-slate-500">Implementation success</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
